import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";

const MAX_VISIBLE = 5;

/**
 * Gösterilecek sayfa numaralarını üretir; 5'ten fazla sayfada "..." ile kısaltır.
 * @returns {Array<number|"...">}
 */
export function buildPageList(currentPage, totalPages) {
  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

/**
 * Sayfa-bazlı sayfalama. currentPage 1-tabanlıdır.
 * Tek sayfa varsa hiç çizilmez — boşuna yer kaplamaz.
 */
export default function ListPagination({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  disabled = false,
}) {
  const { palette } = useClinicTheme();

  if (totalPages <= 1) return null;

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(currentPage * itemsPerPage, totalItems);
  const pages = buildPageList(currentPage, totalPages);

  const navButtonSx = {
    width: 32,
    height: 32,
    borderRadius: 1.5,
    color: COLORS.slate600,
    "&.Mui-disabled": { color: COLORS.slate300 },
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "center", sm: "space-between" },
        gap: 2,
        px: { xs: 1.5, sm: 2.5 },
        py: 1.25,
        borderTop: `1px solid ${COLORS.slate200}`,
        bgcolor: COLORS.white,
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Typography
        sx={{ fontSize: "0.8125rem", color: COLORS.slate500, display: { xs: "none", sm: "block" } }}
      >
        <b>{start}</b>–<b>{end}</b> arası, toplam <b>{totalItems}</b> kayıt
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          aria-label="İlk sayfa"
          disabled={currentPage === 1 || disabled}
          onClick={() => onPageChange?.(1)}
          sx={navButtonSx}
        >
          <FirstPageIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          aria-label="Önceki sayfa"
          disabled={currentPage === 1 || disabled}
          onClick={() => onPageChange?.(currentPage - 1)}
          sx={navButtonSx}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {pages.map((p, i) =>
          p === "..." ? (
            <Box
              key={`gap-${i}`}
              component="span"
              sx={{ px: 0.5, color: COLORS.slate300, fontSize: "0.8125rem" }}
            >
              …
            </Box>
          ) : (
            <Box
              key={p}
              component="button"
              type="button"
              disabled={disabled}
              aria-label={`Sayfa ${p}`}
              aria-current={p === currentPage ? "page" : undefined}
              onClick={() => !disabled && onPageChange?.(p)}
              sx={{
                minWidth: 32,
                height: 32,
                px: 1,
                borderRadius: 1.5,
                border: "none",
                cursor: disabled ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                fontSize: "0.8125rem",
                fontWeight: 700,
                bgcolor: p === currentPage ? palette.primary : "transparent",
                color: p === currentPage ? palette.contrastText : COLORS.slate600,
                "&:hover": {
                  bgcolor: disabled
                    ? p === currentPage
                      ? palette.primary
                      : "transparent"
                    : p === currentPage
                    ? palette.primaryDark
                    : alpha(palette.primary, 0.08),
                },
              }}
            >
              {p}
            </Box>
          )
        )}

        <IconButton
          aria-label="Sonraki sayfa"
          disabled={currentPage === totalPages || disabled}
          onClick={() => onPageChange?.(currentPage + 1)}
          sx={navButtonSx}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          aria-label="Son sayfa"
          disabled={currentPage === totalPages || disabled}
          onClick={() => onPageChange?.(totalPages)}
          sx={navButtonSx}
        >
          <LastPageIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
